import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, FileSignature, Search, Download, Mail, 
  CheckCircle, AlertCircle, Clock, Filter, RefreshCw,
  FileText, Users, Calendar, Zap, Play, Settings
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const AdminAgreementsPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');
  
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    total: 0,
    signed_count: 0,
    unsigned_count: 0,
    signed_partners: [],
    unsigned_partners: []
  });
  const [filter, setFilter] = useState('all'); // all, signed, unsigned
  const [searchTerm, setSearchTerm] = useState('');
  const [sendingReminder, setSendingReminder] = useState(null);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [triggeringReminders, setTriggeringReminders] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    loadAgreements();
    loadSchedulerStatus();
  }, [token, navigate]);

  const loadSchedulerStatus = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/partner-agreement/admin/scheduler-status`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const result = await response.json();
        setSchedulerStatus(result);
      }
    } catch (error) {
      console.error('Scheduler status error:', error);
    }
  };

  const handleTriggerAutoReminders = async () => {
    if (!window.confirm('Möchten Sie jetzt automatische Erinnerungen an alle Partner ohne unterschriebene Vereinbarung senden?')) {
      return;
    }
    
    setTriggeringReminders(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/partner-agreement/admin/trigger-auto-reminders`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        toast.success('Automatische Erinnerungen wurden gesendet');
        loadAgreements(); // Refresh data
        loadSchedulerStatus();
      } else {
        toast.error('Fehler beim Senden der Erinnerungen');
      }
    } catch (error) {
      console.error('Trigger reminders error:', error);
      toast.error('Verbindungsfehler');
    } finally {
      setTriggeringReminders(false);
    }
  };

  const loadAgreements = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/partner-agreement/admin/list`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const result = await response.json();
        setData(result);
      } else {
        toast.error('Fehler beim Laden der Vereinbarungen');
      }
    } catch (error) {
      console.error('Load error:', error);
      toast.error('Verbindungsfehler');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = (partnerId) => {
    window.open(
      `${process.env.REACT_APP_BACKEND_URL}/api/partner-agreement/download/${partnerId}`,
      '_blank'
    );
  };

  const handleSendReminder = async (partner) => {
    setSendingReminder(partner.id);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/partner-agreement/send-reminder`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ partner_id: partner.id })
        }
      );
      
      if (response.ok) {
        toast.success(`Erinnerung an ${partner.email} gesendet`);
      } else {
        toast.error('Fehler beim Senden der Erinnerung');
      }
    } catch (error) {
      console.error('Reminder error:', error);
      toast.error('Verbindungsfehler');
    } finally {
      setSendingReminder(null);
    }
  };

  // Filter and search partners
  const getFilteredPartners = () => {
    let partners = [];
    
    if (filter === 'signed') {
      partners = data.signed_partners || [];
    } else if (filter === 'unsigned') {
      partners = data.unsigned_partners || [];
    } else {
      partners = [...(data.signed_partners || []), ...(data.unsigned_partners || [])];
    }
    
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      partners = partners.filter(p => 
        (p.company_name || '').toLowerCase().includes(search) ||
        (p.email || '').toLowerCase().includes(search)
      );
    }
    
    return partners;
  };

  const filteredPartners = getFilteredPartners();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-green mx-auto mb-4"></div>
          <p className="text-gray-600">Lade Vereinbarungen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/admin/dashboard')}
                className="text-gray-600"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-brand-green/10 rounded-xl">
                  <FileSignature className="w-6 h-6 text-brand-green" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">Partnervereinbarungen</h1>
                  <p className="text-sm text-gray-500">Übersicht aller Verträge</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                onClick={handleTriggerAutoReminders}
                variant="outline"
                size="sm"
                disabled={triggeringReminders || data.unsigned_count === 0}
                className="text-orange-600 border-orange-200 hover:bg-orange-50"
                data-testid="trigger-auto-reminders-btn"
              >
                {triggeringReminders ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Sende...
                  </>
                ) : (
                  <>
                    <Zap className="w-4 h-4 mr-2" />
                    Alle erinnern ({data.unsigned_count})
                  </>
                )}
              </Button>
              <Button
                onClick={loadAgreements}
                variant="outline"
                size="sm"
                className="text-gray-600"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Aktualisieren
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-xl">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Gesamt Partner</p>
                <p className="text-3xl font-bold text-gray-900">{data.total}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-xl">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Unterschrieben</p>
                <p className="text-3xl font-bold text-green-600">{data.signed_count}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-orange-100 rounded-xl">
                <Clock className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Ausstehend</p>
                <p className="text-3xl font-bold text-orange-600">{data.unsigned_count}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters & Search */}
        <div className="bg-white rounded-2xl border border-gray-200 p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Suche nach Firma oder E-Mail..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green"
                data-testid="search-input"
              />
            </div>
            
            {/* Filter Buttons */}
            <div className="flex gap-2">
              <Button
                variant={filter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('all')}
                className={filter === 'all' ? 'bg-brand-green hover:bg-brand-green-dark text-white' : ''}
                data-testid="filter-all"
              >
                <Filter className="w-4 h-4 mr-1" />
                Alle ({data.total})
              </Button>
              <Button
                variant={filter === 'signed' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('signed')}
                className={filter === 'signed' ? 'bg-green-600 hover:bg-green-700 text-white' : 'text-green-600 border-green-200'}
                data-testid="filter-signed"
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                Unterschrieben ({data.signed_count})
              </Button>
              <Button
                variant={filter === 'unsigned' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('unsigned')}
                className={filter === 'unsigned' ? 'bg-orange-600 hover:bg-orange-700 text-white' : 'text-orange-600 border-orange-200'}
                data-testid="filter-unsigned"
              >
                <Clock className="w-4 h-4 mr-1" />
                Ausstehend ({data.unsigned_count})
              </Button>
            </div>
          </div>
        </div>

        {/* Partners List */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          {filteredPartners.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Keine Partner gefunden</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Partner
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Datum
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Aktionen
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredPartners.map((partner) => (
                    <tr key={partner.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-semibold text-gray-900">
                            {partner.company_name || 'Keine Firma'}
                          </p>
                          <p className="text-sm text-gray-500">{partner.email}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {partner.agreement_signed ? (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                            <CheckCircle className="w-3.5 h-3.5" />
                            Unterschrieben
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">
                            <Clock className="w-3.5 h-3.5" />
                            Ausstehend
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {partner.agreement_signed_at ? (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Calendar className="w-4 h-4" />
                            {new Date(partner.agreement_signed_at).toLocaleDateString('de-DE', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          {partner.agreement_signed ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDownloadPdf(partner.id)}
                              className="text-brand-green border-brand-green hover:bg-brand-green/5"
                              data-testid={`download-${partner.id}`}
                            >
                              <Download className="w-4 h-4 mr-1" />
                              PDF
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSendReminder(partner)}
                              disabled={sendingReminder === partner.id}
                              className="text-orange-600 border-orange-200 hover:bg-orange-50"
                              data-testid={`remind-${partner.id}`}
                            >
                              {sendingReminder === partner.id ? (
                                <>
                                  <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                                  Sende...
                                </>
                              ) : (
                                <>
                                  <Mail className="w-4 h-4 mr-1" />
                                  Erinnern
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Info Box */}
        <div className="mt-6 bg-blue-50 rounded-xl p-4 border border-blue-200">
          <div className="flex gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Hinweis zur Partnervereinbarung</p>
              <p>
                Die Partnervereinbarung regelt die Provisionskonditionen (30% vom Netto-Grundhonorar, Brutto-Auszahlung) 
                und muss von jedem Partner vor dem ersten Fall unterschrieben werden.
              </p>
            </div>
          </div>
        </div>

        {/* Scheduler Status Box */}
        {schedulerStatus && (
          <div className="mt-4 bg-green-50 rounded-xl p-4 border border-green-200">
            <div className="flex gap-3">
              <Settings className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-green-800">
                <p className="font-medium mb-1 flex items-center gap-2">
                  Automatische Erinnerungen
                  {schedulerStatus.scheduler_running ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                      <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                      Aktiv
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                      Inaktiv
                    </span>
                  )}
                </p>
                <p>
                  Erinnerungen werden täglich um 10:00 Uhr automatisch an Partner ohne unterschriebene Vereinbarung gesendet.
                  {schedulerStatus.jobs && schedulerStatus.jobs.length > 0 && schedulerStatus.jobs[0].next_run && (
                    <span className="block mt-1">
                      <strong>Nächste Ausführung:</strong>{' '}
                      {new Date(schedulerStatus.jobs[0].next_run).toLocaleString('de-DE', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminAgreementsPage;
